/** Document Library controls: ISO library mode, per-category downline visibility, Super Admin per-ISO access. */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye, EyeOff, Users, Building2 } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const MODES = [['msc', 'Use MSC library', 'Show approved MSC documents and get future updates'],
  ['copy', 'Copy MSC library', 'Take an independent snapshot you manage; MSC updates won\'t change it'],
  ['own', 'Create own library', 'Only your own categories and documents']];
const ROLES = [['iso_agent', 'Agents'], ['iso_subagent', 'Subagents']];

export function LibraryModePanel({ onChanged }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.request('/library/settings').then(setS).catch(() => {}); }, []);
  if (!s || s.is_msc) return null;
  const pick = async (mode) => {
    if (mode === 'copy' && !window.confirm('Copy the MSC categories you have access to into your own library?')) return;
    try { const r = await api.request('/library/settings', { method: 'PUT', body: JSON.stringify({ mode }) }); setS({ ...s, mode: r.mode }); toast.success(mode === 'copy' ? `Copied ${r.categories} categories and ${r.documents} documents` : 'Library mode saved'); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="mb-6 grid gap-3 md:grid-cols-3" data-testid="library-mode-panel">
      {MODES.map(([k, l, d]) => (
        <button key={k} type="button" onClick={() => pick(k)} data-testid={`library-mode-${k}`}
          className={`rounded-xl border p-4 text-left transition-colors ${s.mode === k ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
          <p className="text-sm font-semibold">{l}{s.mode === k && <span className="ml-2 text-xs text-primary">Current</span>}</p>
          <p className="mt-1 text-xs text-muted-foreground">{d}</p>
        </button>))}
    </div>
  );
}

export function VisibilityToggle({ cat, onChanged }) {
  const [open, setOpen] = useState(false);
  const v = cat.visibility || { mode: 'all' };
  const save = async (rule) => {
    try { await api.request(`/library/categories/${cat.id}/visibility`, { method: 'PUT', body: JSON.stringify(rule) }); toast.success('Visibility saved'); setOpen(false); onChanged(); } catch (e) { toast.error(e.message); }
  };
  const label = v.mode === 'hidden' ? 'Hidden from downline' : v.mode === 'custom' ? `Visible to ${(v.roles || []).map((r) => ROLES.find((x) => x[0] === r)?.[1] || r).join(', ') || 'selected people'}` : 'Visible to everyone';
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-muted" data-testid={`category-visibility-${cat.id}`}>
        {v.mode === 'hidden' ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}{label}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="visibility-dialog">
          <DialogHeader><DialogTitle>Who can see “{cat.name}”?</DialogTitle></DialogHeader>
          <Button variant={v.mode === 'all' ? 'default' : 'outline'} onClick={() => save({ mode: 'all' })} data-testid="visibility-all"><Eye className="mr-2 h-4 w-4" />Everyone in my downline</Button>
          {ROLES.map(([r, l]) => <Button key={r} variant={v.mode === 'custom' && (v.roles || []).includes(r) ? 'default' : 'outline'} onClick={() => save({ mode: 'custom', roles: [r] })} data-testid={`visibility-role-${r}`}><Users className="mr-2 h-4 w-4" />Only {l}</Button>)}
          <Button variant={v.mode === 'hidden' ? 'default' : 'outline'} onClick={() => save({ mode: 'hidden' })} data-testid="visibility-hidden"><EyeOff className="mr-2 h-4 w-4" />Hidden from downline</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function IsoAccessButton({ cat, onChanged }) {
  const [isos, setIsos] = useState(null);
  const open = (e) => { e.stopPropagation(); api.request('/library/isos').then(setIsos).catch((er) => toast.error(er.message)); };
  const allowed = (id) => (cat.iso_access || {})[id] ?? (cat.iso_default ?? true);
  const set = async (body) => {
    try { await api.request(`/library/categories/${cat.id}/iso-access`, { method: 'PUT', body: JSON.stringify(body) }); onChanged(); } catch (e) { toast.error(e.message); }
  };
  return (
    <>
      <button type="button" onClick={open} className="flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-muted" data-testid={`category-iso-access-${cat.id}`}><Building2 className="h-3.5 w-3.5" />ISO access</button>
      <Dialog open={!!isos} onOpenChange={(o) => !o && setIsos(null)}>
        <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()} data-testid="iso-access-dialog">
          <DialogHeader><DialogTitle>Which ISOs can use “{cat.name}”?</DialogTitle></DialogHeader>
          <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={cat.iso_default ?? true} onChange={(e) => set({ default: e.target.checked })} data-testid="iso-access-default" />New ISOs get this category</label>
          <div className="max-h-72 space-y-1 overflow-y-auto">{(isos || []).map((t) => (
            <label key={t.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted"><input type="checkbox" checked={allowed(t.id)} onChange={(e) => set({ tenants: { [t.id]: e.target.checked } })} data-testid={`iso-access-${t.id}`} />{t.name}</label>))}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}
